import React from "react"
import Autocomplete from "@material-ui/lab/Autocomplete"
import FullPath from "../../../components/FullPath"
import { Button, CircularProgress, TextField, Typography, Box } from "@material-ui/core"
import { HourReportFormTaskOption } from "../../api/schemas"
import { useGetHourReportTaskOptionsQuery } from "../../api"
import {
  filterOptionsByTitle,
  sortTaskOptionsByTreeAndProjectNumber,
  groupByProjectNumberAndTitle,
} from "./task-options"
import { useTranslation } from "react-i18next"

export const TaskSelector = ({ projectId, selectedTaskId, onChange }: Props) => {
  const { t } = useTranslation("hour-report")
  const {
    currentData: taskOptionsData,
    isFetching: isLoading,
    isError,
    refetch,
  } = useGetHourReportTaskOptionsQuery(projectId, { skip: !projectId })

  const sortedOptions = React.useMemo(
    () => sortTaskOptionsByTreeAndProjectNumber(taskOptionsData?.tasks ?? []),
    [taskOptionsData]
  )
  const selectedTaskOption = sortedOptions.find((option) => option.id === selectedTaskId)

  return (
    <Box mt={2}>
      <Typography variant="subtitle2" gutterBottom>
        {t("form.task", { defaultValue: "Task" })}
      </Typography>
      <Autocomplete
        options={sortedOptions}
        loading={isLoading}
        noOptionsText={t("form.noTasks", { defaultValue: "No tasks in this project" })}
        disabled={isLoading || isError}
        groupBy={groupByProjectNumberAndTitle}
        getOptionLabel={(option) => option.title}
        getOptionSelected={(option, value) => option.id === value.id}
        filterOptions={(options, { inputValue }) => filterOptionsByTitle(options, inputValue)}
        value={selectedTaskOption ?? null}
        onChange={(_, newValue) => onChange(newValue?.id ?? null)}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            name="taskId"
            variant="outlined"
            margin="dense"
            error={isError}
            helperText={
              isError
                ? t("form.tasksLoadError", { defaultValue: "Tasks could not be loaded." })
                : isLoading
                ? t("form.tasksLoading", { defaultValue: "Loading task options" })
                : null
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            fullWidth
          />
        )}
      />
      {isError ? (
        <Button size="small" color="primary" onClick={refetch}>
          {t("actions.retry", { defaultValue: "Retry" })}
        </Button>
      ) : null}
    </Box>
  )
}

const renderOption = (option: HourReportFormTaskOption) => {
  return (
    <Box>
      <FullPath
        projectLink={{ href: "#", label: option.projectTitle, disabled: true }}
        ancestorTaskLinks={option.ancestors.map((task) => ({ ...task, href: "#", disabled: true }))}
        taskLink={{ href: `#`, label: option.title, disabled: true }}
        projectOrTaskNumber={option.taskNumber}
      />
      <Typography variant="body1">{option.title}</Typography>
    </Box>
  )
}

type Props = {
  projectId: string
  selectedTaskId: string | null
  onChange: (taskId: string | null) => void
}
